// DashboardWidgetService — a registry mapping a widget KIND string to its React renderers, mirroring the
// proven FormWidgetService pattern (singleton over a Map, widgets self-register on import). A dashboard
// stores a list of widget INSTANCES ({kind, config}); the page looks each kind up here and renders it.
//
// Each widget kind provides two renderers: a COMPACT view (the tile on the board) and an optional DETAIL
// view (the drill-down when the tile is expanded/opened). This lets one widget reuse the same data at two
// fidelities — e.g. a compact view of signal chips and a detail view of the full panel.

import { ReactNode } from "react";

// DashboardWidgetInstance is one placed widget on a dashboard: its kind + the kind-specific config
// (e.g. {monitorUid} for a pinger). Persisted in the saved layout.
export interface DashboardWidgetInstance {
  // Stable id for this placement (so React keys + remove are stable across reorders).
  id: string;
  kind: string;
  config: Record<string, unknown>;
}

export interface DashboardWidgetRenderProps {
  instance: DashboardWidgetInstance;
  // openDetail asks the dashboard to open this widget's drill-down (detail view). Compact views call it
  // on click; a widget with no detail renderer simply won't be given a clickable affordance.
  openDetail?: () => void;
}

// DashboardWidget is a kind's contract: a label (for the add-widget menu), a compact renderer (the tile),
// an optional detail renderer (the drill-down), and an optional config form to gather what a new instance
// needs (e.g. pick which monitor). title() derives a per-instance heading from its config.
export interface DashboardWidget {
  kind: string;
  label: string;
  title: (instance: DashboardWidgetInstance) => string;
  renderCompact: (props: DashboardWidgetRenderProps) => ReactNode;
  renderDetail?: (props: DashboardWidgetRenderProps) => ReactNode;
}

export class DashboardWidgetService {
  static readonly _instance: DashboardWidgetService = new DashboardWidgetService();

  static get(): DashboardWidgetService {
    return this._instance;
  }

  private widgets = new Map<string, DashboardWidget>();

  register(widget: DashboardWidget): void {
    this.widgets.set(widget.kind, widget);
  }

  get(kind: string): DashboardWidget | undefined {
    return this.widgets.get(kind);
  }

  list(): DashboardWidget[] {
    return [...this.widgets.values()];
  }
}
